// A single "ever above 0.2" bit cannot tell a dead mic from a quiet one from an SDR who
// simply did not speak. This keeps how much of the call the mic spent at each level.
//
// The input is the SDK's `volume` level, time-weighted from the gaps between samples:
// the emit cadence is a timer, not a contract, and a throttled tab stretches it.

// A throttled tab can deliver samples seconds apart; crediting one stale reading for
// that long would dominate the call.
const MAX_GAP_MS = 250;

// Bin i covers (EDGES[i-1], EDGES[i]]; bin 0 is exactly zero, which only digital silence
// reaches (the analyser floors at −100 dBFS).
const LEVEL_EDGES = [0, 0.02, 0.05, 0.1, 0.2, 0.3, 0.5, 0.7, 1] as const;
const SIGNAL_MIN = 0.02;
const VOICE_MIN = 0.2;

interface MicLevel {
  binsMs: number[];
  totalMs: number;
  signalMs: number;
  voiceMs: number;
  max: number | null;
  lastAt: number | null;
  lastInput: number | null;
}

export function newMicLevel(): MicLevel {
  return {
    binsMs: LEVEL_EDGES.map(() => 0),
    totalMs: 0,
    signalMs: 0,
    voiceMs: 0,
    max: null,
    lastAt: null,
    lastInput: null,
  };
}

function binOf(input: number): number {
  if (input <= 0) return 0;
  const i = LEVEL_EDGES.findIndex((edge, idx) => idx > 0 && input <= edge);
  return i === -1 ? LEVEL_EDGES.length - 1 : i;
}

// Credits the PREVIOUS sample's value for the gap since it.
export function observeLevel(s: MicLevel, input: number, now: number): void {
  if (s.lastAt != null && s.lastInput != null) {
    const gap = Math.min(Math.max(0, now - s.lastAt), MAX_GAP_MS);
    const bin = binOf(s.lastInput);
    s.binsMs[bin] = (s.binsMs[bin] ?? 0) + gap;
    s.totalMs += gap;
    if (s.lastInput > SIGNAL_MIN) s.signalMs += gap;
    if (s.lastInput > VOICE_MIN) s.voiceMs += gap;
  }
  s.max = s.max == null ? input : Math.max(s.max, input);
  s.lastAt = now;
  s.lastInput = input;
}

export interface MicLevelReport {
  mic_level_ms: number;
  mic_level_max: number | null;
  mic_level_p50: number | null;
  mic_level_p95: number | null;
  mic_level_zero_ratio: number | null;
  mic_level_signal_ratio: number | null;
  mic_level_voice_ratio: number | null;
}

const round = (v: number, dp: number): number => Math.round(v * 10 ** dp) / 10 ** dp;

function percentile(s: MicLevel, q: number): number {
  const target = s.totalMs * q;
  let acc = 0;
  for (let i = 0; i < s.binsMs.length; i++) {
    acc += s.binsMs[i]!;
    if (acc >= target) return LEVEL_EDGES[i]!;
  }
  return LEVEL_EDGES[LEVEL_EDGES.length - 1]!;
}

export function reportLevel(s: MicLevel): MicLevelReport {
  const ms = Math.round(s.totalMs);
  if (s.totalMs <= 0) {
    return {
      mic_level_ms: 0,
      mic_level_max: null,
      mic_level_p50: null,
      mic_level_p95: null,
      mic_level_zero_ratio: null,
      mic_level_signal_ratio: null,
      mic_level_voice_ratio: null,
    };
  }
  return {
    mic_level_ms: ms,
    mic_level_max: s.max == null ? null : round(s.max, 3),
    mic_level_p50: percentile(s, 0.5),
    mic_level_p95: percentile(s, 0.95),
    mic_level_zero_ratio: round(s.binsMs[0]! / s.totalMs, 2),
    mic_level_signal_ratio: round(s.signalMs / s.totalMs, 2),
    mic_level_voice_ratio: round(s.voiceMs / s.totalMs, 2),
  };
}
